import { createClient } from '@supabase/supabase-js';

// Essas variáveis de ambiente devem ser configuradas no arquivo .env
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Criação do cliente Supabase (apenas para acesso ao banco de dados)
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Chave para armazenar a sessão no localStorage
const SESSION_STORAGE_KEY = 'ride_df_user_session';

// Funções de autenticação usando a tabela personalizada 'users'
export const signUpUser = async (email, password, userData = {}) => {
  try {
    // Verificar se o usuário já existe
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('email')
      .eq('email', email)
      .single();
    
    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 é o código para 'não encontrado'
      throw checkError;
    }
    
    if (existingUser) {
      throw new Error('Usuário com este email já existe');
    }
    
    // Criar novo usuário na tabela personalizada
    const { data, error } = await supabase
      .from('users')
      .insert([{
        email,
        password, // Nota: em produção, você deve usar bcrypt ou similar para hash
        name: userData.name || '',
        role: userData.role || 'user'
      }])
      .select()
      .single();
    
    if (error) throw error;
    
    // Armazenar sessão no localStorage
    const session = {
      user: data,
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 horas
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    
    return { success: true, data };
  } catch (error) {
    console.error('Error signing up:', error);
    return { success: false, error: error.message };
  }
};

export const signInUser = async (email, password) => {
  try {
    // Primeiro, verificar se o email existe
    const { error: emailError } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .single();
    
    if (emailError && emailError.code === 'PGRST116') { // Email não encontrado
      return { success: false, error: 'Email ou senha inválidos' };
    }
    
    // Se o email existe, verificar a senha
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .eq('password', password) // Nota: em produção, você deve verificar hash
      .single();
    
    if (error || !data) {
      return { success: false, error: 'Email ou senha inválidos' };
    }
    
    // Armazenar sessão no localStorage
    const session = {
      user: data,
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 horas
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    
    return { success: true, data: { user: data, session } };
  } catch (error) {
    console.error('Error signing in:', error);
    return { success: false, error: error.message };
  }
};

export const signOutUser = async () => {
  try {
    // Remover sessão do localStorage
    localStorage.removeItem(SESSION_STORAGE_KEY);
    return { success: true };
  } catch (error) {
    console.error('Error signing out:', error);
    return { success: false, error: error.message };
  }
};

export const getCurrentUser = async () => {
  try {
    // Obter sessão do localStorage
    const sessionStr = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionStr) {
      return { success: false, error: 'Nenhuma sessão encontrada' };
    }
    
    const session = JSON.parse(sessionStr);
    
    // Verificar se a sessão expirou
    if (new Date(session.expires_at) < new Date()) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return { success: false, error: 'Sessão expirada' };
    }
    
    // Buscar dados atualizados do usuário
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', session.user.id)
      .single();
    
    if (error) throw error;
    
    // Atualizar sessão com dados atualizados
    session.user = data;
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    
    return { success: true, data: data };
  } catch (error) {
    console.error('Error getting current user:', error);
    return { success: false, error: error.message };
  }
};

export const getCurrentSession = async () => {
  try {
    // Obter sessão do localStorage
    const sessionStr = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionStr) {
      return { success: false, data: null };
    }
    
    const session = JSON.parse(sessionStr);
    
    // Verificar se a sessão expirou
    if (new Date(session.expires_at) < new Date()) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return { success: false, data: null };
    }
    
    return { success: true, data: session };
  } catch (error) {
    console.error('Error getting session:', error);
    return { success: false, error: error.message };
  }
};

// Funções auxiliares para interagir com o Supabase

// Função para salvar respostas de formulário
export const saveFormResponses = async (formType, responses, userId, status = 'draft') => {
  try {
    // Primeiro verifica se já existe um rascunho
    const { data: existingDraft, error: queryError } = await supabase
      .from('form_responses')
      .select('id')
      .eq('form_type', formType)
      .eq('user_id', userId)
      .eq('status', 'draft')
      .maybeSingle();

    if (queryError) throw queryError;

    // Se existir um rascunho, faz UPDATE
    if (existingDraft) {
      const updateData = {
        responses,
        status
      };
      
      // Tenta adicionar updated_at se a coluna existir
      try {
        updateData.updated_at = new Date().toISOString();
      } catch (e) {
        console.log('Coluna updated_at não disponível');
      }
      
      const { data, error } = await supabase
        .from('form_responses')
        .update(updateData)
        .eq('id', existingDraft.id);

      if (error) throw error;
      return { success: true, data };
    }
    
    // Se não existir, faz INSERT
    const { data, error } = await supabase
      .from('form_responses')
      .insert([{
        form_type: formType,
        responses,
        user_id: userId,
        status,
        created_at: new Date().toISOString()
      }]);

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error saving form responses:', error);
    return { success: false, error: error.message };
  }
};

// Função para obter respostas de formulário de um usuário específico
export const getUserFormResponses = async (userId, formType = null) => {
  try {
    let query = supabase
      .from('form_responses')
      .select('*')
      .eq('user_id', userId);
    
    if (formType) {
      query = query.eq('form_type', formType);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching user form responses:', error);
    return { success: false, error: error.message };
  }
};

// Função para obter todas as respostas de formulário (para administradores)
export const getAllFormResponses = async () => {
  try {
    // Primeiro busca as respostas
    const { data: responses, error: responsesError } = await supabase
      .from('form_responses')
      .select('*')
      .order('created_at', { ascending: false });

    if (responsesError) throw responsesError;
    
    // Extrai os IDs únicos de usuários
    const userIds = [...new Set(responses.map(r => r.user_id))];
    
    // Busca os dados dos usuários
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('id, name, email')
      .in('id', userIds);
    
    if (usersError) throw usersError;
    
    // Combina os dados
    const usersMap = new Map(users.map(user => [user.id, user]));
    const enrichedResponses = responses.map(response => ({
      ...response,
      user: usersMap.get(response.user_id)
    }));
    
    return { success: true, data: enrichedResponses };
  } catch (error) {
    console.error('Error fetching all form responses:', error);
    return { success: false, error: error.message };
  }
};

// Função para obter o estado (habilitado/desabilitado) de todos os formulários.
// Retorna um mapa { form_id: is_active }. Formulários sem entrada na tabela
// são considerados habilitados (default seguro).
export const getFormSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('form_settings')
      .select('form_id, is_active');

    if (error) throw error;

    const settingsMap = {};
    (data || []).forEach((row) => {
      settingsMap[row.form_id] = row.is_active;
    });

    return { success: true, data: settingsMap };
  } catch (error) {
    console.error('Error fetching form settings:', error);
    return { success: false, error: error.message };
  }
};

// Função para habilitar/desabilitar um formulário (apenas admin).
// O controle de admin é feito no frontend (user.role === 'admin') e a
// escrita vai direto via anon key, seguindo o mesmo padrão das demais
// funções deste módulo. Quando a auth for migrada para JWT/Supabase Auth,
// mover esta chamada para o backend com checagem de role server-side.
export const setFormActive = async (formId, isActive, userId = null) => {
  try {
    const { data, error } = await supabase
      .from('form_settings')
      .upsert(
        {
          form_id: formId,
          is_active: isActive,
          updated_at: new Date().toISOString(),
          updated_by: userId,
        },
        { onConflict: 'form_id' }
      )
      .select('form_id, is_active')
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error updating form settings:', error);
    return { success: false, error: error.message };
  }
};